import {Component, inject} from '@angular/core';
import {AutoUnsubscribe} from "../../../../infrastructure/decorators/auto-unsubscribe.decorator";
import {GridEventOutput, GridModel} from "../../../shared/model/page-grid";
import {TramsTripGridService} from "../../service/trams-trip-grid.service";
import {TramsTripDataResponse, TramsTripResponse} from "../../model/trams-trip";
import {TramsDataSourceService} from "../../../payments/service/trams-data-source.service";
import {TramsDataSourceModel} from "../../../payments/models/trams-data-source";
import {Router} from "@angular/router";
import {DatePipe} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {TripDetailsModalComponent} from "../trip-details-modal/trip-details-modal.component";

@Component({
    selector: 'app-trams-trip',
    templateUrl: './trams-trip.component.html',
    standalone: false
})
@AutoUnsubscribe()
export class TramsTripComponent {
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);
  private readonly dataSourceService = inject(TramsDataSourceService);
  private readonly dialog = inject(MatDialog);

  private columnStyles = (item: TramsTripDataResponse) => {
    if (!item.adxTripReference || !item.tripId) return {'background-color': '#FFFFFF'};
    return {'background-color': '#FAD4D5'}
  }

  protected readonly clientBalanceGrid: GridModel<TramsTripResponse> = {
    gridDataTestId: 'trams-trip-list',
    gridTitle: 'Fms Trams Trips',
    replayButton: true,
    columns: [
      {
        columnProperty: 'tripId',
        columnHeader: 'Fms Trip Number',
        sort: true,
        clickable: true,
        linkEventFunction: (item: TramsTripDataResponse) => `Trip/${item.tripId}`,
        style: this.columnStyles
      },
      {
        columnProperty: 'adxTripReference',
        columnHeader: 'Invoice Record Locator',
        sort: true,
        style: this.columnStyles
      },
      {
        columnProperty: 'tramsRecordLocator',
        columnHeader: 'Trams Record Locator',
        sort: true,
        clickable: true,
        linkEventFunction: (item: TramsTripDataResponse) => `Invoices/TramsInvoices?invoiceRecordLocator=${item.tramsRecordLocator}`,
        style: this.columnStyles
      },
      {
        columnProperty: 'fmsStatus',
        columnHeader: 'Fms Trip Status',
        sort: true,
        style: this.columnStyles
      },
      {
        columnProperty: 'fmsTripStartDate',
        columnHeader: 'Trip Start Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'MM/dd/yyyy') ?? '',
        style: this.columnStyles
      },
      {
        columnProperty: 'fmsTripEndDate',
        columnHeader: 'Trip End Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'MM/dd/yyyy') ?? '',
        style: this.columnStyles
      },
      {
        columnProperty: 'tramsInvoiceCount',
        columnHeader: 'Invoices Count',
        sort: true,
        style: this.columnStyles
      },
      {
        columnProperty: 'fmsInvoiceCount',
        columnHeader: 'Fms Invoices Count',
        sort: true,
        style: this.columnStyles
      },
      {
        columnProperty: 'fmsDataSourceName',
        columnHeader: 'Fms Data Source Name',
        sort: true,
        style: this.columnStyles
      },
      {
        columnProperty: 'tramsMainPassengerName',
        columnHeader: 'Main Passenger',
        sort: true,
        style: this.columnStyles
      },
    ],
    filters: [
      {
        filterProperty: 'fmsTripStartDate',
        filterDisplayName: 'Trip Start Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'fmsTripEndDate',
        filterDisplayName: 'Trip Start Date To',
        filterType: 'DATE',
        dependsOn: {
          property: 'fmsTripStartDate',
          errorMessage: 'Please select “Trip Start Date From“'
        },
      },
      {
        filterProperty: 'adxTripReference',
        filterDisplayName: 'Invoice Record Locator',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'TramsTrip',
          autocompleteColumnName: 'adxTripReference',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'tramsRecordLocator',
        filterDisplayName: 'Trams Record Locator',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'TramsTrip',
          autocompleteColumnName: 'tramsRecordLocator',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'fmsDataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.dataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.sourceName
        }
      },
    ],
    gridGetService: inject(TramsTripGridService)
  };

  private openDialog(tripId: number) {
    const dialog = this.dialog.open(TripDetailsModalComponent, {width: '80%'});
    dialog.componentInstance.tripId.set(tripId);
  }

  protected handleItemClick({column, item}: GridEventOutput<TramsTripDataResponse>): void {
    if (column === 'tripId') this.openDialog(item.tripId);
    if (column === 'tramsRecordLocator') this.router.navigate(['Invoices', 'TramsInvoices'], {
      queryParams: {
        invoiceRecordLocator: item.tramsRecordLocator
      }
    });
  }

}
